import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { formatShortDate } from '@/lib/format';

interface AuditEvent {
  id: string;
  principalId: string;
  principalTier: 'manager' | 'store_ops';
  authMethod: 'entra' | 'pin';
  action: string;
  entityType: string;
  entityId: string;
  before: unknown;
  after: unknown;
  correlationId: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface ListResponse {
  events: AuditEvent[];
  nextCursor: string | null;
}

/**
 * Admin audit log viewer. Architecture §Phase-2C: CTO-level single dashboard
 * of everyone who's doing what. Restricted to Admins + CLevel.
 */
export function AdminAuditLog() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Reset (filter-changed) and append (load-more) are separate so that the
  // effect dependencies don't include `cursor` — otherwise every page tick
  // would re-fire the reset. The functions share the URL builder.
  const buildParams = useCallback(
    (cursorVal: string | null) => {
      const p = new URLSearchParams();
      if (action) p.set('action', action);
      if (entityType) p.set('entityType', entityType);
      if (principalId) p.set('principalId', principalId);
      if (cursorVal) p.set('cursor', cursorVal);
      return p;
    },
    [action, entityType, principalId],
  );

  const loadFresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch<ListResponse>(`/audit-log?${buildParams(null).toString()}`);
      setEvents(r.events);
      setCursor(r.nextCursor);
      setHasMore(r.nextCursor !== null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  async function loadMore() {
    if (!cursor) return;
    setLoading(true);
    try {
      const r = await apiFetch<ListResponse>(`/audit-log?${buildParams(cursor).toString()}`);
      setEvents((prev) => [...prev, ...r.events]);
      setCursor(r.nextCursor);
      setHasMore(r.nextCursor !== null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadFresh();
  }, [loadFresh]);

  return (
    <div className="min-h-screen p-6 max-w-6xl mx-auto">
      <header className="border-b border-border pb-4 mb-4">
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <p className="text-sm text-muted-foreground">
          Every state-changing call across the system. Restricted to Admins + CLevel.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <input
          placeholder="action (e.g. cash_request.approved)"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        />
        <input
          placeholder="entityType (e.g. cash_request)"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        />
        <input
          placeholder="principalId (oid or barista uuid)"
          value={principalId}
          onChange={(e) => setPrincipalId(e.target.value)}
          className="border border-border rounded-md px-2 py-1 text-sm font-mono"
        />
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}

      <table className="w-full text-xs">
        <thead className="text-left text-muted-foreground border-b border-border">
          <tr>
            <th className="py-2 w-44">When</th>
            <th>Action</th>
            <th>Entity</th>
            <th>Principal</th>
            <th>Tier · Auth</th>
            <th>IP</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const isOpen = expanded === e.id;
            return (
              <>
                <tr
                  key={e.id}
                  className="border-b border-border/40 align-top cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpanded(isOpen ? null : e.id)}
                >
                  <td className="py-2">{formatShortDate(e.createdAt)}</td>
                  <td>
                    <code>{e.action}</code>
                  </td>
                  <td>
                    {e.entityType}
                    <span className="text-muted-foreground"> · </span>
                    <code className="text-muted-foreground">{e.entityId.slice(0, 8)}…</code>
                  </td>
                  <td>
                    <code className="text-muted-foreground">{e.principalId.slice(0, 12)}…</code>
                  </td>
                  <td>
                    {e.principalTier} · {e.authMethod}
                  </td>
                  <td className="text-muted-foreground">{e.ip ?? '—'}</td>
                  <td>{isOpen ? '▾' : '▸'}</td>
                </tr>
                {isOpen ? (
                  <tr key={`${e.id}-detail`} className="border-b border-border/40">
                    <td colSpan={7} className="py-3 px-2 bg-muted/20">
                      <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                        <div>
                          <div className="text-muted-foreground mb-1">before</div>
                          <pre className="whitespace-pre-wrap break-all">
                            {e.before === null ? '—' : JSON.stringify(e.before, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <div className="text-muted-foreground mb-1">after</div>
                          <pre className="whitespace-pre-wrap break-all">
                            {e.after === null ? '—' : JSON.stringify(e.after, null, 2)}
                          </pre>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        correlation_id: <code>{e.correlationId}</code>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </>
            );
          })}
          {!loading && events.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-6 text-center text-muted-foreground">
                No audit events match the filter.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {hasMore ? (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loading}
            className="px-4 py-1.5 border border-border rounded-md text-sm disabled:opacity-60"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
